import { FC, useEffect } from 'react'
import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { Modal } from 'Components/Modal/Modal'
import { StyledInput } from 'Components/Inputs/StyledInput'
import { StyledInputLabel } from 'Components/Inputs/StyledInputLabel'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ButtonContainer, spacingMD } from 'Utils/styles/spacing'
import { fontSizeMD, fontWeightBold } from 'Utils/styles/text'
import { useUpdatePricingDescriptionQuery } from 'Hooks/api/JOONAdmin/useUpdatePricingDescriptionQuery'
import { EmployerCountry } from 'Utils/types/employerInfo'
import { CountryNames } from 'Utils/types/countries'

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacingMD};
`

const Title = styled.div`
  font-size: ${fontSizeMD};
  font-weight: ${fontWeightBold};
`

type FormValues = { pricingDescription: string }

type EditPricingDescriptionModalProps = {
  showModal: boolean
  setShowModal: (show: boolean) => void
  countryConfig: EmployerCountry
}

export const EditPricingDescriptionModal: FC<EditPricingDescriptionModalProps> = ({
  showModal,
  setShowModal,
  countryConfig
}) => {
  const { countryCode, employerId, pricingDescription } = countryConfig
  const { mutateAsync: updateDescription, isLoading } = useUpdatePricingDescriptionQuery()

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { pricingDescription: pricingDescription ?? '' }
  })

  useEffect(() => {
    if (showModal) reset({ pricingDescription: pricingDescription ?? '' })
  }, [showModal, pricingDescription, reset])

  const onSubmit = async (data: FormValues) => {
    const response = await updateDescription({
      employerId,
      countryCode,
      pricingDescription: data.pricingDescription
    })
    if (response.success) setShowModal(false)
  }

  return (
    <Modal showModal={showModal} setShowModal={setShowModal} maxWidth={480} minWidth={480}>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <Title>Edit pricing description: {CountryNames[countryCode]}</Title>
        <StyledInputLabel>Pricing description</StyledInputLabel>
        <Controller
          control={control}
          name="pricingDescription"
          render={({ field: { onChange, value } }) => (
            <StyledInput value={value} onChange={onChange} />
          )}
        />
        <ButtonContainer>
          <StyledButton primary id="save.label" isLoading={isLoading} />
        </ButtonContainer>
      </FormContainer>
    </Modal>
  )
}
